import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import CandidatesPage from './CandidatesPage';
import CandidateReportsList from './CandidateReportsList';
import CandidateReport from './CandidateReport';

const INTERVIEW_FACTORS = {};

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.avtechbmt.cloud/api/candidates';

export default function CandidateModule() {
  const { token } = useAuth();
  const [currentPage, setCurrentPage] = useState('candidates'); // 'candidates' | 'reports' | 'report'
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const activeToken = token || localStorage.getItem('token');
      const res = await fetch(API_BASE_URL, {
        headers: {
          'Content-Type': 'application/json',
          ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
        },
      });
      const contentType = res.headers.get('content-type') || '';
      const textData = await res.text();
      if (!res.ok || !contentType.includes('application/json')) {
        throw new Error(
          textData.startsWith('<!DOCTYPE')
            ? `Server returned HTML (${res.status}). Verify backend URL.`
            : `API error (${res.status}): ${textData}`
        );
      }
      const data = JSON.parse(textData);
      if (data.success) setStudents(data.data || []);
      else setError(data.message || 'Failed to fetch candidates');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const activeStudent = students.find((s) => s._id === selectedCandidateId);

  /* ----------------------------- Render ----------------------------- */
  if (loading) return <SkeletonWorkspace />;

  if (error) {
    return (
      <div className="cm-error-shell" role="alert">
        <div className="cm-error-card">
          <h3>⚠️ Candidate Module Loading Failure</h3>
          <p>{error}</p>
          <button className="cv-btn cv-btn-primary" onClick={fetchCandidates}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  /* --- Page: list of reports for a candidate --- */
  if (currentPage === 'reports' && activeStudent) {
    return (
      <CandidateReportsList
        candidate={activeStudent}
        onBack={() => {
          setCurrentPage('candidates');
          setSelectedCandidateId(null);
        }}
        onOpenReport={(reportId) => {
          setSelectedReportId(reportId);
          setCurrentPage('report');
          window.scrollTo({ top: 0, behavior: 'instant' });
        }}
        onReportsChanged={fetchCandidates}
      />
    );
  }

  /* --- Page: single report view --- */
  if (currentPage === 'report' && activeStudent && selectedReportId) {
    return (
      <CandidateReport
        candidateId={activeStudent._id}
        reportId={selectedReportId}
        onBack={() => {
          setCurrentPage('reports');
          setSelectedReportId(null);
        }}
      />
    );
  }

  /* --- Page: candidate list (default) --- */
  return (
    <CandidatesPage
      students={students}
      setStudents={setStudents}
      onViewReports={(id) => {
        setSelectedCandidateId(id);
        setCurrentPage('reports');
        window.scrollTo({ top: 0, behavior: 'instant' });
      }}
      onRefresh={fetchCandidates}
    />
  );
}

/* --------------------------- Tiny fallbacks --------------------------- */
function SkeletonWorkspace() {
  return (
    <div className="candidate-page" aria-busy="true">
      <div className="cv-skeleton-toolbar" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="cv-skeleton-card" />
      ))}
    </div>
  );
}